import React, { useState, useEffect, useCallback, useMemo } from "react";
import AdminLedgerService from "../../services/admin/AdminLedgerService";
import AdminHeadService from "../../services/admin/AdminHeadService";
import AdminUserService from "../../services/admin/AdminUserService";
import AdminLedgerViewModal from "../../components/admin/AdminLedgerViewModal";
import AdminLedgerListing from "../../components/admin/AdminLedgerListing";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye } from "@fortawesome/free-solid-svg-icons";
import { useLoading } from "../../context/LoadingContext";
import { showErrorToast } from "../../utils/AlertUtils";
import guLocale from "../../locales/gu";

const AdminLedger = () => {
  const locale = guLocale.ledgerListing;
  const { setIsLoading } = useLoading();
  const [filterText, setFilterText] = useState("");
  const [ledgers, setLedgers] = useState([]);
  const [showViewModal, setShowViewModal] = useState(false);
  const [currentLedger, setCurrentLedger] = useState(null);
  const [userData, setUserData] = useState([]);
  const [headData, setHeadData] = useState([]);
  const headFilterOptions = { showHeads: true, showUsers: true };
  const isFilterVisible = true;
  const [selectedFilters, setSelectedFilters] = useState({ user: [], head: [] });

  const getPaymentMethodText = (method) => {
    switch (method) {
      case "cash":
        return locale.paymentMethodCash;
      case "online":
        return locale.paymentMethodOnline;
      case "cheque":
        return locale.paymentMethodCheque;
      default:
        return method;
    }
  };

  const columns = [
    {
      name: locale.accountDetails, // Changed name to "Details" to reflect combined info
      cell: (row) => (
        <div className="py-2">
          {/* Added padding to the cell for height */}
          <div className="text-xs text-gray-500">
            {/* Border and padding for date */}
            {new Date(row.transaction_date).toLocaleDateString('en-GB')}
            <span className="badge badge-primary text-xs ml-1">
            { getPaymentMethodText(row.payment_method) }
            </span>
          </div>
          <div className="text-bold">{row?.head_id?.head_name}</div>
        </div>
      ),
      sortable: true,
      width: "350px",
    },
    {
      name: locale.details,
      selector: (row) => row.details,
      sortable: true,
      width: "300px",
    },
    {
      name: locale.amount,
      cell: (row) => {
        // Add cell function
        const type = row.transaction_type;
        const amount = row.amount;
        if (type === "IN") {
          return <span className="text-bold text-success">₹{amount}</span>;
        } else {
          return <span className="text-bold text-danger">₹{amount}</span>;
        }
      },
      sortable: true,
      width: "150px",
    },
    {
      name: locale.type,
      cell: (row) => {
        // Add cell function
        const type = row.transaction_type;
        if (type === "OUT") {
          return <span className="badge badge-danger text-xs">{locale.out}</span>;
        } else if (type === "IN") {
          return <span className="badge badge-success text-xs">{locale.in}</span>;
        } else {
          return <span>{type}</span>; // Default case, in case of other types
        }
      },
      sortable: true,
      width: "150px",
    },
    {
      name: locale.action,
      cell: (row) => (
        <>
          <button
            className="btn btn-info btn-sm mr-2" // Added info button for View
            onClick={() => handleView(row)} // handleView function
          >
            <FontAwesomeIcon icon={faEye} />
          </button>
        </>
      ),
      width: "120px",
    },
  ];

  const fetchAll = useCallback(async () => {
    try {
      setIsLoading(true);
      const [ledgersRes, usersRes, headsRes] = await Promise.all([
        AdminLedgerService.getLedgers(),
        AdminUserService.getUsers(),
        AdminHeadService.getHeads(),
      ]);
      if (ledgersRes && ledgersRes.statusCode === 200) {
        setLedgers(ledgersRes.data);
      }
      if (headsRes && headsRes.statusCode === 200 && Array.isArray(headsRes.data)) {
        const headMap = new Map();
        headsRes.data.forEach((head) => {
          const key = head.head_name.toLowerCase();
          if (!headMap.has(key)) {
            headMap.set(key, head);
          }
        });
        const uniqueHeads = Array.from(headMap.values());
        setHeadData(uniqueHeads);
      }
      if (usersRes && usersRes.statusCode === 200) {
        setUserData(usersRes.data);
      }
    } catch (error) {
      console.error("Error fetching data", error);
      showErrorToast(error.message);
    } finally {
      setIsLoading(false);
    }
  }, [setIsLoading]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const filteredData = useMemo(() => {
    const lowerFilterText = filterText.toLowerCase();
  
    return ledgers.filter((item) => {
      const transactionDetails = `${new Date(item.transaction_date).toLocaleDateString()} ${item.payment_method} ${item.head_id?.head_name || ""}`.toLowerCase();
      const detailsMatch = transactionDetails.includes(lowerFilterText);
  
      const descriptionMatch = item.details?.toLowerCase().includes(lowerFilterText);
      const amountMatch = item.amount?.toString().toLowerCase().includes(lowerFilterText);
      const transactionTypeMatch = item.transaction_type?.toLowerCase().includes(lowerFilterText);
  
      const matchesUser =
        selectedFilters.user.length === 0 ||
        selectedFilters.user.some((u) => u.value === item?.user_id);
  
      const matchesHead =
        selectedFilters.head.length === 0 ||
        selectedFilters.head.some((h) => h.value === item?.head_id?._id);
  
      return (detailsMatch || descriptionMatch || amountMatch || transactionTypeMatch) && matchesUser && matchesHead;
    });
  }, [ledgers, filterText, selectedFilters]);  

  const handleFilterChange = useCallback((e) => {
    setFilterText(e.target.value); // Only update filterText state
  }, []);

  const handleView = async (ledger) => {
    setIsLoading(true);
    try {
      const response = await AdminLedgerService.getLedgerDetails(ledger._id); // API call to get ledger by ID
      if (response && response.data) {
        setCurrentLedger(response.data); // Set the current ledger with fetched data
        setShowViewModal(true); // Show the modal to view details
      } else {
        showErrorToast("Failed to fetch ledger details.");
      }
    } catch (error) {
      console.error("Error fetching ledger", error);
      showErrorToast(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleModalClose = () => {
    setShowViewModal(false);
    setCurrentLedger(null);
  };

  const handleApplyFilters = useCallback((filterArrays) => {
      setSelectedFilters(filterArrays);
  }, []);

  return (
    <div>
      <AdminLedgerListing
        columns={columns}
        data={filteredData}
        handleFilterChange={handleFilterChange}
        filterText={filterText}
        userData={userData}
        headData={headData}
        headFilterOptions={headFilterOptions}
        isFilterVisible={isFilterVisible}
        handleApplyFilters={handleApplyFilters}
      />
      <AdminLedgerViewModal
        show={showViewModal}
        handleClose={handleModalClose}
        initialData={currentLedger}
      />
    </div>
  );
};

export default AdminLedger;
